import type { AccountInfo } from "@azure/msal-browser";
import { Role } from "@ask-hr/core/roles";
import { useEffect, useState } from "react";
import { Admin } from "./admin/Admin.js";
import { ChatPage } from "./chat/ChatPage.js";
import { rolesOf, signOut } from "./auth/msal.js";
import { SiteHeader } from "./components/SiteHeader.js";
import { UserMenu } from "./components/UserMenu.js";
import { DEFAULT_PREFS, applyTheme, loadPrefs, resetPrefs, savePrefs, type Prefs } from "./prefs.js";
import { linkTo, navigate, usePath } from "./router.js";
import { Settings } from "./Settings.js";

// One sign-in, three views like sites.novara.com: Ask HR for everyone and Admin for HR as header
// buttons, Settings in the user menu. The API checks roles on every admin call; the tab is convenience.
type View = "ask" | "admin" | "settings";

function viewOf(path: string): View {
  if (path === "/admin" || path.startsWith("/admin/")) return "admin";
  if (path === "/settings") return "settings";
  return "ask";
}

export function App({ account }: { account: AccountInfo }) {
  const roles = rolesOf(account);
  const isAdmin = roles.includes(Role.Admin);
  const isAgent = roles.includes(Role.Agent); // HR.Admin includes HR.Agent
  const [prefs, setPrefs] = useState<Prefs>(() => {
    const p = loadPrefs();
    // Start page: only when arriving at the root, so links and reloads keep their page.
    if (p.landing === "admin" && isAgent && window.location.pathname === "/") {
      window.history.replaceState(null, "", "/admin");
    }
    return p;
  });
  const path = usePath();
  const view = viewOf(path);

  useEffect(() => {
    applyTheme(prefs.theme);
  }, [prefs.theme]);

  const update = (p: Partial<Prefs>) => {
    const next = { ...prefs, ...p };
    setPrefs(next);
    savePrefs(next);
  };

  const tabs: [View, string, string][] = [
    ["ask", "Ask HR", "/"],
    ...(isAgent ? [["admin", "Admin", "/admin"] as [View, string, string]] : []),
  ];

  return (
    <>
      <SiteHeader
        title={view === "admin" ? "Ask HR Admin" : "Ask HR"}
        account={
          <UserMenu
            account={{ name: account.name || account.username, email: account.username }}
            settingsHref="/settings"
            settingsActive={view === "settings"}
            onSettings={() => navigate("/settings")}
            onSignOut={() => void signOut()}
          />
        }
      >
        {tabs.map(([id, label, to]) => (
          <a key={id} className="setbtn" href={to} onClick={linkTo(to)} aria-current={view === id ? "page" : undefined}>
            {label}
          </a>
        ))}
      </SiteHeader>
      <main className="wrap">
        {view === "admin" ? (
          isAgent ? (
            <Admin path={path} isAdmin={isAdmin} />
          ) : (
            <section className="panel">
              <h2>Not available</h2>
              <p className="sub">Admin is for HR.</p>
            </section>
          )
        ) : view === "settings" ? (
          <Settings
            prefs={prefs}
            admin={isAgent}
            update={update}
            reset={() => {
              resetPrefs();
              setPrefs(DEFAULT_PREFS);
            }}
          />
        ) : (
          <ChatPage path={path} firstName={(account.name ?? account.username).split(" ")[0] ?? ""} />
        )}
      </main>
    </>
  );
}
